import { APP_ROUTER } from '@/routes/routes';
import { Typography } from 'antd';
import { useNavigate } from 'react-router-dom';

const { Text, Paragraph } = Typography;

// 色の意味
const C = {
  llm: { fill: '#f9f0ff', stroke: '#b37feb', text: '#531dab' }, // Anthropic
  img: { fill: '#f6ffed', stroke: '#95de64', text: '#389e0d' }, // OpenAI 画像
  local: { fill: '#fafafa', stroke: '#d9d9d9', text: '#595959' }, // ローカル処理
  db: { fill: '#e6f4ff', stroke: '#69b1ff', text: '#0958d9' }, // 保存
};

type StepKind = 'llm' | 'img' | 'local' | 'db';
type Step = {
  n: string;
  kind: StepKind;
  title: string;
  sub: string;
  /** クリックで編集ページに飛ぶプロンプト key */
  promptKeys?: string[];
};

const STEPS: Step[] = [
  {
    n: '1',
    kind: 'llm',
    title: 'テーマ選定',
    sub: 'claude-opus-4-7 ・ Web検索あり（朝刊/夕刊と共有・1日1回）',
    promptKeys: ['theme.user'],
  },
  {
    n: '2',
    kind: 'llm',
    title: '漫才台本を生成',
    sub: 'claude-opus-4-7',
    promptKeys: ['manzai.system', 'manzai.user'],
  },
  {
    n: '3',
    kind: 'local',
    title: 'パース（テキスト→構造化）',
    sub: 'LLMなし ・ TITLE/DUO/セリフ/KOTOBA を抽出',
  },
  {
    n: '4',
    kind: 'local',
    title: '検証（品質チェック）',
    sub: 'LLMなし ・ NGなら②へ戻る（最大2回リトライ）',
  },
  {
    n: '5',
    kind: 'llm',
    title: 'サムネ用プロンプト生成',
    sub: 'claude-sonnet-4-6',
    promptKeys: ['thumb.manzai.system', 'thumb.manzai.user'],
  },
  {
    n: '6',
    kind: 'local',
    title: '参照画像を取得',
    sub: 'LLMなし ・ ボケ/ツッコミのアバター画像を取得',
  },
  {
    n: '7',
    kind: 'img',
    title: 'カバー画像を生成',
    sub: 'gpt-image-2 (images.edit) ・ 参照画像2枚 + プロンプト',
  },
  {
    n: '8',
    kind: 'db',
    title: 'DB に保存',
    sub: 'status = published ・ S3 アップロード後 coverImageId を紐付け',
  },
];

const BOX_X = 150;
const BOX_W = 540;
const BOX_H = 76;
const GAP = 28;
const TOP = 16;
const stepPitch = BOX_H + GAP;

const boxY = (i: number) => TOP + i * stepPitch;
const VIEW_H = boxY(STEPS.length - 1) + BOX_H + 16;

export const ManzaiFlowDiagram: React.FC = () => {
  const navigate = useNavigate();

  const goPrompt = (key: string) =>
    navigate(`${APP_ROUTER.KAZOKUGOTO.PROMPTS}?key=${encodeURIComponent(key)}`);

  return (
    <div>
      <svg
        viewBox={`0 0 720 ${VIEW_H}`}
        width='100%'
        style={{ maxWidth: 720, fontFamily: 'inherit' }}
        role='img'
        aria-label='漫才の生成フロー図'
      >
        <defs>
          <marker
            id='arrow'
            markerWidth='10'
            markerHeight='10'
            refX='6'
            refY='3'
            orient='auto'
            markerUnits='strokeWidth'
          >
            <path d='M0,0 L6,3 L0,6 Z' fill='#8c8c8c' />
          </marker>
          <marker
            id='arrow-retry'
            markerWidth='10'
            markerHeight='10'
            refX='6'
            refY='3'
            orient='auto'
            markerUnits='strokeWidth'
          >
            <path d='M0,0 L6,3 L0,6 Z' fill='#fa8c16' />
          </marker>
        </defs>

        {/* 縦の接続矢印 */}
        {STEPS.slice(0, -1).map((_, i) => {
          const y1 = boxY(i) + BOX_H;
          const y2 = boxY(i + 1);
          return (
            <line
              key={`arr-${i}`}
              x1={BOX_X + BOX_W / 2}
              y1={y1}
              x2={BOX_X + BOX_W / 2}
              y2={y2 - 2}
              stroke='#8c8c8c'
              strokeWidth={1.5}
              markerEnd='url(#arrow)'
            />
          );
        })}

        {/* 検証NG → 台本生成 への リトライ矢印（左回り込み） */}
        {(() => {
          const fromY = boxY(3) + BOX_H / 2; // 検証(4)
          const toY = boxY(1) + BOX_H / 2; // 台本生成(2)
          const leftX = 60;
          return (
            <g>
              <path
                d={`M ${BOX_X} ${fromY} L ${leftX} ${fromY} L ${leftX} ${toY} L ${BOX_X - 2} ${toY}`}
                fill='none'
                stroke='#fa8c16'
                strokeWidth={1.5}
                strokeDasharray='5 3'
                markerEnd='url(#arrow-retry)'
              />
              <text
                x={leftX - 6}
                y={(fromY + toY) / 2}
                fontSize='11'
                fill='#fa8c16'
                textAnchor='middle'
                transform={`rotate(-90 ${leftX - 6} ${(fromY + toY) / 2})`}
              >
                NGならリトライ（最大2回）
              </text>
            </g>
          );
        })()}

        {/* skipCover スキップ範囲の注記（5〜7） */}
        {(() => {
          const y1 = boxY(4);
          const y2 = boxY(6) + BOX_H;
          const x = BOX_X + BOX_W + 12;
          return (
            <g>
              <line
                x1={x}
                y1={y1}
                x2={x}
                y2={y2}
                stroke='#bfbfbf'
                strokeWidth={1.5}
              />
              <text
                x={x + 6}
                y={(y1 + y2) / 2}
                fontSize='11'
                fill='#8c8c8c'
                transform={`rotate(90 ${x + 6} ${(y1 + y2) / 2})`}
                textAnchor='middle'
              >
                skipCover=true でスキップ
              </text>
            </g>
          );
        })()}

        {/* 各ステップのボックス */}
        {STEPS.map((s, i) => {
          const y = boxY(i);
          const c = C[s.kind];
          return (
            <g key={s.n}>
              <rect
                x={BOX_X}
                y={y}
                width={BOX_W}
                height={BOX_H}
                rx={8}
                fill={c.fill}
                stroke={c.stroke}
                strokeWidth={1.5}
              />
              {/* ステップ番号の丸 */}
              <circle
                cx={BOX_X + 26}
                cy={y + BOX_H / 2}
                r={13}
                fill={c.stroke}
              />
              <text
                x={BOX_X + 26}
                y={y + BOX_H / 2 + 4}
                fontSize='13'
                fill='#fff'
                textAnchor='middle'
                fontWeight='bold'
              >
                {s.n}
              </text>
              <text
                x={BOX_X + 50}
                y={y + 24}
                fontSize='14'
                fill={c.text}
                fontWeight='bold'
              >
                {s.title}
              </text>
              <text x={BOX_X + 50} y={y + 44} fontSize='11.5' fill='#595959'>
                {s.sub}
              </text>
              {/* プロンプト key（クリックで編集ページへ） */}
              {s.promptKeys && (
                <text x={BOX_X + 50} y={y + 64} fontSize='12'>
                  <tspan fill='#8c8c8c'>prompt: </tspan>
                  {s.promptKeys.map((k, ki) => (
                    <tspan key={k}>
                      {ki > 0 && <tspan fill='#bfbfbf'> + </tspan>}
                      <tspan
                        fill='#1677ff'
                        style={{ cursor: 'pointer' }}
                        textDecoration='underline'
                        onClick={() => goPrompt(k)}
                      >
                        {k}
                      </tspan>
                    </tspan>
                  ))}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* 凡例 */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8 }}>
        <LegendItem color={C.llm.stroke} label='Anthropic（テキスト生成）' />
        <LegendItem color={C.img.stroke} label='OpenAI（画像生成）' />
        <LegendItem color={C.local.stroke} label='ローカル処理（LLMなし）' />
        <LegendItem color={C.db.stroke} label='DB 保存' />
      </div>

      <Paragraph type='secondary' style={{ marginTop: 8, marginBottom: 0 }}>
        青字のプロンプト key をクリックすると、その履歴・編集画面が開きます。
      </Paragraph>

      <Paragraph style={{ marginTop: 16, marginBottom: 0 }}>
        <Text strong>1日あたりの LLM 呼び出し（漫才分）:</Text>
        <ul style={{ marginTop: 4, marginBottom: 0 }}>
          <li>
            claude-opus-4-7 … テーマ選定 ×1（朝刊/夕刊と共有）＋ 台本生成 ×1〜3（検証リトライ含む）
          </li>
          <li>claude-sonnet-4-6 … サムネ用プロンプト生成 ×1</li>
          <li>gpt-image-2 … カバー画像生成 ×1</li>
        </ul>
      </Paragraph>
      <Paragraph type='secondary' style={{ marginTop: 8, marginBottom: 0 }}>
        ※ プロンプトはすべて「プロンプト」ページで編集でき、変更は次回以降の生成から反映されます。
        「カバー画像をスキップ」を ON にすると ⑤〜⑦ を飛ばしてテキストのみ生成します。
      </Paragraph>
      <Paragraph type='secondary' style={{ marginTop: 8, marginBottom: 0 }}>
        ※ 「生成する」ボタンを押すと、上の一連の処理は<Text strong>バックグラウンドジョブ</Text>
        として実行されます（数分かかるため）。生成ページはジョブの状態をポーリングし、完了で結果を表示します。
        進捗は<Text strong>テーマ選定 → 朝刊 → 夕刊 → 漫才</Text>
        のフェーズ単位で表示され、各フェーズが「待機 → 生成中 → 完了 / 失敗」と遷移します。
        カバー画像（⑤〜⑦）も非同期で生成され、アイテム一覧では coverStatus が
        「生成中 → 生成済み / 失敗」と遷移します。
      </Paragraph>
    </div>
  );
};

const LegendItem: React.FC<{ color: string; label: string }> = ({
  color,
  label,
}) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <span
      style={{
        width: 14,
        height: 14,
        borderRadius: 3,
        background: color,
        display: 'inline-block',
      }}
    />
    <Text style={{ fontSize: 12 }}>{label}</Text>
  </span>
);
